const parseLocalizedNumber = (value) => {
  const source = String(value ?? '')
    .trim()
    .replace(/[^0-9,.-]/g, '');

  if (!source) return null;

  const lastComma = source.lastIndexOf(',');
  const lastDot = source.lastIndexOf('.');
  let normalized = source;

  if (lastComma !== -1 && lastDot !== -1) {
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    const thousandsSeparator = decimalSeparator === ',' ? /\./g : /,/g;

    normalized = source
      .replace(thousandsSeparator, '')
      .replace(decimalSeparator, '.');
  } else if (lastComma !== -1) {
    const decimalDigits = source.length - lastComma - 1;

    normalized =
      decimalDigits > 0 && decimalDigits <= 2
        ? source.replace(',', '.')
        : source.replace(/,/g, '');
  } else if (lastDot !== -1) {
    const decimalDigits = source.length - lastDot - 1;

    normalized =
      decimalDigits > 0 && decimalDigits <= 2
        ? source
        : source.replace(/\./g, '');
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
};

class FBPriceRange extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.minRange = this.querySelector('[data-fb-price-min]');
    this.maxRange = this.querySelector('[data-fb-price-max]');
    this.priceFacet = this.closest('price-facet-component');
    this.minField = this.priceFacet?.querySelector('[ref="minInput"]');
    this.maxField = this.priceFacet?.querySelector('[ref="maxInput"]');
    this.rangeMax = Number(this.dataset.rangeMax || this.maxRange?.max || 0);

    if (
      !(this.minRange instanceof HTMLInputElement) ||
      !(this.maxRange instanceof HTMLInputElement) ||
      !(this.minField instanceof HTMLInputElement) ||
      !(this.maxField instanceof HTMLInputElement) ||
      !Number.isFinite(this.rangeMax) ||
      this.rangeMax <= 0
    ) {
      return;
    }

    this.dataset.initialized = 'true';

    this.minRange.addEventListener('input', (event) => {
      event.stopPropagation();
      this.#syncFromRange('min');
    });

    this.maxRange.addEventListener('input', (event) => {
      event.stopPropagation();
      this.#syncFromRange('max');
    });

    this.minRange.addEventListener('change', (event) => {
      event.stopPropagation();
      this.#commitRange('min');
    });

    this.maxRange.addEventListener('change', (event) => {
      event.stopPropagation();
      this.#commitRange('max');
    });

    this.minField.addEventListener('input', () => {
      this.#syncFromFields('min');
    });

    this.maxField.addEventListener('input', () => {
      this.#syncFromFields('max');
    });

    this.minField.addEventListener('change', () => {
      this.#syncFromFields('min');
    });

    this.maxField.addEventListener('change', () => {
      this.#syncFromFields('max');
    });

    this.#syncFromFields();
  }

  #getValues() {
    let minValue = Number(this.minRange.value);
    let maxValue = Number(this.maxRange.value);

    if (!Number.isFinite(minValue)) minValue = 0;
    if (!Number.isFinite(maxValue)) maxValue = this.rangeMax;

    minValue = Math.max(0, Math.min(minValue, this.rangeMax));
    maxValue = Math.max(0, Math.min(maxValue, this.rangeMax));

    return { minValue, maxValue };
  }

  #normalizeValues(source) {
    let { minValue, maxValue } = this.#getValues();

    if (minValue > maxValue) {
      if (source === 'min') {
        minValue = maxValue;
      } else {
        maxValue = minValue;
      }
    }

    this.minRange.value = String(minValue);
    this.maxRange.value = String(maxValue);

    return { minValue, maxValue };
  }

  #writeFields(minValue, maxValue) {
    /*
     * En los extremos dejamos el campo vacío para que Shopify no
     * añada filtros innecesarios a la URL. El placeholder sigue
     * mostrando 0 y el precio máximo.
     */
    this.minField.value = minValue > 0 ? this.#formatValue(minValue) : '';
    this.maxField.value =
      maxValue < this.rangeMax ? this.#formatValue(maxValue) : '';
  }

  #formatValue(value) {
    if (Number.isInteger(value)) return String(value);

    return String(Math.round(value * 100) / 100);
  }

  #syncFromRange(source) {
    const { minValue, maxValue } = this.#normalizeValues(source);

    this.#writeFields(minValue, maxValue);
    this.#paint(minValue, maxValue);
  }

  #commitRange(source) {
    this.#syncFromRange(source);

    const sourceField = source === 'min' ? this.minField : this.maxField;

    /*
     * Un único change hace que Horizon vuelva a calcular los
     * productos usando los dos campos ya sincronizados.
     */
    sourceField.dispatchEvent(
      new Event('change', {
        bubbles: true,
      })
    );
  }

  #syncFromFields(source) {
    const parsedMin = parseLocalizedNumber(this.minField.value);
    const parsedMax = parseLocalizedNumber(this.maxField.value);

    let minValue = Math.max(
      0,
      Math.min(parsedMin ?? 0, this.rangeMax)
    );

    let maxValue = Math.max(
      0,
      Math.min(parsedMax ?? this.rangeMax, this.rangeMax)
    );

    if (minValue > maxValue) {
      if (source === 'min') {
        minValue = maxValue;
      } else {
        maxValue = minValue;
      }
    }

    this.minRange.value = String(minValue);
    this.maxRange.value = String(maxValue);

    this.#paint(minValue, maxValue);
  }

  #paint(
    minValue = Number(this.minRange.value),
    maxValue = Number(this.maxRange.value)
  ) {
    const minPercent = Math.max(
      0,
      Math.min(100, (minValue / this.rangeMax) * 100)
    );

    const maxPercent = Math.max(
      0,
      Math.min(100, (maxValue / this.rangeMax) * 100)
    );

    this.style.setProperty('--fb-price-min', `${minPercent}%`);
    this.style.setProperty('--fb-price-max', `${maxPercent}%`);

    /*
     * Hace accesible el control inferior cuando los dos tiradores
     * están muy próximos.
     */
    this.minRange.style.zIndex = minPercent > 85 ? '5' : '3';
    this.maxRange.style.zIndex = minPercent > 85 ? '4' : '3';
  }
}

if (!customElements.get('fb-price-range')) {
  customElements.define('fb-price-range', FBPriceRange);
}
